/**
 * Main Application Component
 * 
 * This file handles:
 * - Global providers (error boundary, auth, notifications)
 * - Application shell layout (sidebar, header, footer)
 * - Mobile responsiveness shell
 * 
 * Route definitions are extracted to src/router/routes.tsx
 */

import React from 'react';
import './App.css';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { cn } from './lib/utils';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';
import { UniversalErrorBoundary } from './components/UniversalErrorBoundary';
import AuthGate from './components/AuthGate';
import { ErrorDialogProvider } from './context/ErrorDialogContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { MessagesProvider } from './context/MessagesContext';
import { readEnvBoolean, readEnvString } from './lib/env';
import { MobileShell } from './components/mobile/MobileShell';
import { MobileUnsupportedScreen } from './components/mobile/MobileUnsupportedScreen';
import { isPathAllowedOnMobile } from './components/mobile/navigation';
import { useIsMobileActive } from './hooks/useIsMobileActive';
import { useChunkLoadRecovery } from './hooks/useChunkLoadRecovery';
import { AppContentRoutes, isFullWidthRoute, LoginPage } from './router';
import { TooltipProvider } from './components/ui/tooltip';
import { SuccessToastHost } from './components/SuccessToastHost';

type AppShellProps = {
  environmentLabel?: string;
};

// Phone sessions do not need the desktop navigation/header utilities.
const AppSidebar = React.lazy(() => import('./components/AppSidebar'));
const AppHeader = React.lazy(() => import('./components/AppHeader'));

/**
 * Main application shell with sidebar layout.
 * Handles desktop and mobile rendering modes.
 */
function AppShell({ environmentLabel }: AppShellProps) {
  const location = useLocation();
  const isMobileActive = useIsMobileActive();
  useChunkLoadRecovery();

  // Mobile shell rendering
  if (isMobileActive) {
    const mobileContent = isPathAllowedOnMobile(location.pathname) ? (
      <>
        <div className="flex w-full flex-col gap-6">
          <AppContentRoutes />
        </div>
        <AppFooter />
      </>
    ) : (
      <MobileUnsupportedScreen />
    );

    return (
      <div className="[--header-height:64px]">
        <MobileShell>{mobileContent}</MobileShell>
      </div>
    );
  }

  // Desktop shell rendering
  const isFullWidth = isFullWidthRoute(location.pathname);
  const isEstimatesRoute = location.pathname.startsWith('/estimates');
  const isDashboardRoute = location.pathname === '/';

  return (
    <div className="[--header-height:64px]">
      <SidebarProvider className={cn('flex min-h-screen flex-col bg-background text-foreground')}>
        <React.Suspense fallback={<div role="status" className="p-4 text-sm text-muted-foreground">Loading workspace…</div>}>
          <AppHeader environmentLabel={environmentLabel} />
          <div className="flex flex-1 overflow-hidden">
            <AppSidebar />
            <SidebarInset className="flex flex-1 flex-col overflow-hidden">
              <div
                className={cn(
                  'w-full flex-1 overflow-y-auto px-4 pb-16 pt-6 sm:px-6 lg:px-10',
                  'flex w-full flex-col gap-6',
                  isFullWidth ? undefined : 'mx-auto',
                  isFullWidth
                    ? undefined
                    : (isEstimatesRoute || isDashboardRoute ? undefined : 'max-w-5xl')
                )}
              >
                <AppContentRoutes />
              </div>
              <AppFooter />
            </SidebarInset>
          </div>
        </React.Suspense>
      </SidebarProvider>
    </div>
  );
}

/**
 * Application footer with navigation links.
 */
function AppFooter() {
  return (
    <footer className="App-footer-simple border-t border-border bg-background py-6 text-sm text-muted-foreground">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-2 px-4">
        <span className="font-medium text-foreground">RH Tools</span>
        <nav className="flex flex-row items-center gap-2 text-xs sm:text-sm">
          <Link className="transition-colors hover:text-foreground" to="/guides">
            Guides Library
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link className="transition-colors hover:text-foreground" to="/about">
            About
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link className="transition-colors hover:text-foreground" to="/privacy">
            Privacy Policy
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link className="transition-colors hover:text-foreground" to="/terms">
            Terms &amp; Conditions
          </Link>
        </nav>
      </div>
    </footer>
  );
}

/**
 * Root application component.
 * Sets up global providers and routing.
 */
export default function App() {
  const configuredLabel = readEnvString('VITE_ENVIRONMENT_LABEL');

  let environmentLabel = configuredLabel;
  if (!environmentLabel && typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host.includes('localhost') || host.startsWith('127.')) {
      environmentLabel = 'LOCAL';
    } else if (host.includes('dev') || host.includes('staging')) {
      environmentLabel = 'STAGING';
    }
  }

  return (
    <UniversalErrorBoundary
      fallback={
        <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center text-sm text-muted-foreground">
          <div className="space-y-3">
            <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
            <p>Please refresh the page or try again later. The error has been logged for review.</p>
          </div>
        </div>
      }
    >
      <ErrorDialogProvider>
        <SuccessToastHost />
        <TooltipProvider delayDuration={150}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/*"
              element={(
                <AuthGate>
                  <NotificationsProvider>
                    <MessagesProvider>
                      <AppShell environmentLabel={environmentLabel} />
                    </MessagesProvider>
                  </NotificationsProvider>
                </AuthGate>
              )}
            />
          </Routes>
        </TooltipProvider>
      </ErrorDialogProvider>
    </UniversalErrorBoundary>
  );
}
